/*
 * sidebar-options-item-menu
 */
import { Link, useLocation } from "@tanstack/react-router"
import { cn } from "@emr/react-ui"
import {
	Button,
	Tooltip,
	TooltipContent,
	TooltipProvider,
	TooltipTrigger,
} from "@emr/react-ui"
import { ExternalLink } from "lucide-react"
import type { ReactNode } from "react"
import { CollapseMenuButton } from "./sidebar-collapse-menu-button"
import { useIsInSheetSidebar, useSheetClose } from "./sidebar.context"
import { useSidebarToggle } from "./use-sidebar-toggle"

type Submenu = {
	to: string
	label: string
}

interface SidebarOptionsItemMenuProps {
	label: string
	to: string
	submenus: Submenu[]
	Icon: ReactNode
	children?: ReactNode
	className?: string
}

export function SidebarOptionsItemMenu({
	label,
	to,
	Icon,
	submenus,
	className,
}: SidebarOptionsItemMenuProps) {
	// Use CSS media queries instead of JavaScript for better SSR compatibility
	const sidebar = useSidebarToggle()
	const location = useLocation()
	const isInSheet = useIsInSheetSidebar()
	const closeSheet = useSheetClose()

	const { pathname } = location
	const finalSidebarIsOpen = isInSheet || sidebar.isOpen

	function handleOnClick(_e: any) {
		// Close sheet when clicking menu items on mobile
		if (isInSheet && closeSheet) {
			closeSheet()
		}
	}

	if (submenus?.length && submenus.length > 0) {
		const active = submenus.some((submenu) => pathname.startsWith(submenu.to))
		return (
			<div className={cn("w-full", className)}>
				<CollapseMenuButton
					Icon={Icon}
					pathname={pathname}
					label={label}
					active={active}
					submenus={submenus}
					isOpen={finalSidebarIsOpen}
					// setIsOpen={sidebar?.setIsOpen}
				/>
			</div>
		)
	}

	return (
		<div className={cn("w-full", className)}>
			<TooltipProvider disableHoverableContent>
				<Tooltip delayDuration={100}>
					<TooltipTrigger asChild>
						<Button
							variant={pathname.startsWith(to) ? "outline" : "ghost"}
							className={
								"text-paper-foreground mb-1 h-10 w-full justify-start rounded-none border-none pl-6"
							}
							asChild
							onClick={handleOnClick}
						>
							<Link
								to={to}
								target={to.includes("https://") ? "_blank" : undefined}
							>
								{/* Option - Link */ ""}
								<span
									className={cn("ml-[3px]", !finalSidebarIsOpen ? "" : "mr-4")}
								>
									{Icon}
								</span>
								<p
									className={cn(
										"max-w-full flex-1 truncate",
										!finalSidebarIsOpen
											? "-translate-x-96 opacity-0"
											: "translate-x-0 opacity-100",
									)}
								>
									{label}
								</p>
								{to.includes("https://") && (
									<span className={!finalSidebarIsOpen ? "hidden" : ""}>
										<ExternalLink className={"size-4"} />
									</span>
								)}
								{/* Option - Link */ ""}
							</Link>
						</Button>
					</TooltipTrigger>
					{!finalSidebarIsOpen && (
						<TooltipContent side={"right"}>{label}</TooltipContent>
					)}
				</Tooltip>
			</TooltipProvider>
		</div>
	)
}
